import { CSSProperties } from 'react';
import { TAlignment, TRowHeight, TColumnWidthMode, TColumnWidthValue, TColumnWidthModeProp } from '../types';

/**
 * 将 align 对齐方式转换为 text-align CSS 值
 * @param align 对齐方式
 * @returns text-align CSS 值
 */
const alignToTextAlign = (align: TAlignment): 'left' | 'center' | 'right' => {
  switch (align) {
    case 'start':
      return 'left';
    case 'center':
      return 'center';
    case 'end':
      return 'right';
    case 'stretch':
      return 'left';
    default:
      return 'left';
  }
};

/**
 * 生成列宽 CSS 值
 * @param mode 列宽模式或任意 CSS 宽度值
 * @returns CSS 列宽值
 */
const generateColumnWidth = (mode: TColumnWidthValue): string => {
  if (typeof mode === 'number') {
    return `${mode}px`;
  }

  // 字符串：优先原样返回，允许任意合法 CSS 宽度值（如 1fr / 120px / minmax(...)）
  if (typeof mode === 'string' && mode) {
    const presets: Record<TColumnWidthMode, string> = {
      auto: 'auto',
      'min-content': 'min-content',
      'max-content': 'max-content',
      'fit-content': 'fit-content(0)',
      minmax: 'minmax(min-content, max-content)',
    };
    return presets[mode as TColumnWidthMode] || mode;
  }

  // 兜底
  return 'minmax(min-content, max-content)';
};

/**
 * 根据数组配置生成 gridTemplateColumns
 * 不足列用默认值 'minmax' 填充
 */
const generateTemplateFromArray = (
  modes: TColumnWidthValue[],
  columns: number
): string => {
  const defaultMode: TColumnWidthMode = 'minmax';
  const normalized = Array.from({ length: columns }).map((_, idx) => {
    const val = modes[idx];
    if (val === undefined || val === null) {
      return generateColumnWidth(defaultMode);
    }

    if (typeof val === 'string' || typeof val === 'number') {
      return generateColumnWidth(val);
    }

    return generateColumnWidth(val);
  });

  return normalized.join(' ');
};

/**
 * 解析最终的 gridTemplateColumns，优先级：数组 > 用户样式 > 单值模式
 * 数组不足列用默认值 'minmax' 填充
 */
export const resolveGridTemplateColumns = (
  columnWidthMode: TColumnWidthModeProp,
  columns: number,
  userTemplate?: string,
  fallbackMode: TColumnWidthMode = 'minmax'
): string => {
  if (Array.isArray(columnWidthMode)) {
    return generateTemplateFromArray(columnWidthMode, columns);
  }

  if (userTemplate) {
    return userTemplate;
  }

  return `repeat(${columns}, ${generateColumnWidth(columnWidthMode || fallbackMode)})`;
};

/**
 * 生成网格样式对象
 * @param columns 列数
 * @param gap 间距
 * @param alignment 对齐方式
 * @param rowHeight 行高
 * @param maxHeight 最大高度
 * @param maxWidth 最大宽度
 * @param columnWidthMode 列宽模式
 * @param rowGap 行间距（优先级高于 gap 的行间距部分）
 * @returns CSS 样式对象
 */
export const generateGridStyle = (
  columns: number,
  gap?: string | number,
  alignment?: TAlignment,
  rowHeight?: TRowHeight,
  maxHeight?: string | number,
  maxWidth?: string | number,
  columnWidthMode: TColumnWidthModeProp = 'minmax',
  verticalAlign: TAlignment = 'center',
  gridTemplateColumns?: string,
  rowGap?: string | number
): CSSProperties => {
  const style: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: gridTemplateColumns || `repeat(${columns}, ${generateColumnWidth(columnWidthMode as TColumnWidthMode)})`,
    alignItems: verticalAlign || 'center', // 默认垂直居中对齐
    justifyItems: alignment || 'start', // 默认水平左对齐
    overflow: 'hidden', // 防止内容溢出
  };

  // 处理间距：如果设置了 rowGap，则分别设置行间距和列间距；否则使用 gap
  if (rowGap !== undefined) {
    style.rowGap = typeof rowGap === 'number' ? `${rowGap}px` : rowGap;
    if (gap !== undefined) {
      style.columnGap = typeof gap === 'number' ? `${gap}px` : gap;
    }
  } else if (gap !== undefined) {
    style.gap = typeof gap === 'number' ? `${gap}px` : gap;
  }

  // 设置行高
  if (rowHeight && rowHeight !== 'auto') {
    style.gridAutoRows = typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight;
  }

  // 设置最大高度和宽度
  if (maxHeight) {
    style.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    style.overflowY = 'auto';
  }

  if (maxWidth) {
    style.maxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
    style.overflowX = 'auto';
  }

  return style;
};

/**
 * 生成网格项样式对象
 * @param colSpan 跨列数
 * @param rowSpan 跨行数
 * @param align 对齐方式
 * @param verticalAlign 垂直对齐方式
 * @returns CSS 样式对象
 */
export const generateItemStyle = (
  colSpan?: number,
  rowSpan?: number,
  align?: TAlignment,
  verticalAlign?: TAlignment
): CSSProperties => {
  const style: CSSProperties = {};

  if (colSpan && colSpan > 1) {
    style.gridColumn = `span ${colSpan}`;
  }

  if (rowSpan && rowSpan > 1) {
    style.gridRow = `span ${rowSpan}`;
  }

  if (align) {
    style.justifySelf = align;
    // 同步设置 text-align
    style.textAlign = alignToTextAlign(align);
  }

  // 垂直对齐：优先使用显式 verticalAlign，其次保持对齐与 align 一致（向后兼容）
  if (verticalAlign) {
    style.alignSelf = verticalAlign;
  } else if (align) {
    style.alignSelf = align;
  }

  return style;
};

/**
 * 验证跨列跨行参数
 * @param colSpan 跨列数
 * @param rowSpan 跨行数
 * @param columns 总列数
 * @returns 验证结果
 */
export const validateSpan = (
  colSpan?: number,
  rowSpan?: number,
  columns?: number
): { isValid: boolean; message?: string; } => {
  if (colSpan && colSpan < 1) {
    return { isValid: false, message: 'colSpan 必须大于 0' };
  }

  if (rowSpan && rowSpan < 1) {
    return { isValid: false, message: 'rowSpan 必须大于 0' };
  }

  if (columns && colSpan && colSpan > columns) {
    return { isValid: false, message: `colSpan (${colSpan}) 不能大于总列数 (${columns})` };
  }

  return { isValid: true };
};

/**
 * 生成 CSS 类名
 * @param baseClass 基础类名
 * @param modifiers 修饰符
 * @param customClass 自定义类名
 * @returns 合并后的类名
 */
export const generateClassName = (
  baseClass: string,
  modifiers: Record<string, boolean> = {},
  customClass?: string
): string => {
  const classes = [baseClass];
  
  Object.entries(modifiers).forEach(([key, value]) => {
    if (value) {
      classes.push(`${baseClass}--${key}`);
    }
  });
  
  if (customClass) {
    classes.push(customClass);
  }
  
  return classes.join(' ');
};